import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import './StudentDashboard.css';
import Toast from '../faculty/Toast.jsx';
import Loader from '../faculty/Loader.jsx';
import WeekLearningView from './WeekLearningView';
import StudentCommunication from '../communication/StudentCommunication';
import StudentCertificates from './StudentCertificates';
import ProgressRing from './ProgressRing';

const sectionTitles = {
  dashboard: 'My Courses',
  browse: 'Browse Courses',
  learning: 'Course Learning',
  profile: 'My Profile',
  certificates: 'My Certificates',
  messages: 'Messages',
};

const StudentDashboard = () => {
  const navigate = useNavigate();
  // Navigation & UI
  const [activeSection, setActiveSection] = useState('dashboard');
  const [sidebarOpen, setSidebarOpen] = useState(true);
  
  // Data
  const [enrolledCourses, setEnrolledCourses] = useState([]);
  const [availableCourses, setAvailableCourses] = useState([]);
  const [faculties, setFaculties] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [studentProfile, setStudentProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState(null);
  
  // Student Info
  const [studentId] = useState(localStorage.getItem('std_id'));
  const [studentName, setStudentName] = useState(localStorage.getItem('student_name') || 'Student');
  const [actualStudentId, setActualStudentId] = useState(null);
  
  // Filters
  const [facultyFilter, setFacultyFilter] = useState('');
  const [departmentFilter, setDepartmentFilter] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  
  // Course Details
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [courseDetails, setCourseDetails] = useState(null);
  const [weekProgress, setWeekProgress] = useState([]);

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      
      let resolvedStudentId = studentId;
      let fallbackProfile = null;
      
      if (!resolvedStudentId) {
        const user = localStorage.getItem('user');
        if (user) {
          try {
            const userData = JSON.parse(user);
            if (userData.user_id) {
              const studentRes = await axios.get(`/api/students/by-user/${userData.user_id}`);
              resolvedStudentId = studentRes.data.std_id || studentRes.data.student_id;
              setActualStudentId(resolvedStudentId);
              fallbackProfile = studentRes.data || null;
              setStudentProfile(fallbackProfile);
              setStudentName(fallbackProfile?.std_name || fallbackProfile?.student_name || localStorage.getItem('student_name') || 'Student');
            }
          } catch (e) {
            console.error('[StudentDashboard] Error extracting student ID:', e);
          }
        }
      } else {
        setActualStudentId(resolvedStudentId);
      }
      
      if (!resolvedStudentId) {
        throw new Error('Student ID not found. Please login again.');
      }
      
      // Fetch all data in parallel
      const [enrolledRes, availableRes, facultiesRes, departmentsRes, profileRes] = await Promise.all([
        axios.get(`/api/enrollments/student/${resolvedStudentId}`),
        axios.get('/api/courses?publish_status=published'),
        axios.get('/api/faculties').catch(err => ({ data: [] })), // Fallback to empty array
        axios.get('/api/departments').catch(err => ({ data: [] })), // Fallback to empty array
        axios.get(`/api/students/${resolvedStudentId}/profile`).catch(err => ({ data: null })) // Fallback to null
      ]);

      setEnrolledCourses(enrolledRes.data || []);
      setFaculties(Array.isArray(facultiesRes.data) ? facultiesRes.data : []);
      setDepartments(Array.isArray(departmentsRes.data) ? departmentsRes.data : []);
      setStudentProfile(profileRes.data || fallbackProfile || null);
      setStudentName(
        profileRes.data?.student_name ||
        profileRes.data?.std_name ||
        fallbackProfile?.student_name ||
        fallbackProfile?.std_name ||
        localStorage.getItem('student_name') ||
        'Student'
      );
      
      // Filter enrolled courses
      const enrolledIds = new Set((enrolledRes.data || []).map(e => e.course_id));
      const available = (availableRes.data || []).filter(c => !enrolledIds.has(c.course_id));
      setAvailableCourses(available);
      
    } catch (error) {
      console.error('[StudentDashboard] Error loading data:', error);
      setToast({ type: 'error', message: 'Failed to load dashboard: ' + error.message });
    } finally {
      setLoading(false);
    }
  };

  // Filter available courses
  const filteredCourses = (availableCourses || []).filter(course => {
    const matchesFaculty = !facultyFilter || course.fac_id === facultyFilter;
    const matchesDepartment = !departmentFilter || 
      (faculties.find(f => f.fac_id === course.fac_id)?.dept_id === departmentFilter);
    const matchesSearch = !searchTerm || 
      course.course_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      course.course_title?.toLowerCase().includes(searchTerm.toLowerCase());
    
    return matchesFaculty && matchesDepartment && matchesSearch;
  });

  // Get unique faculties for current department filter
  const filteredFaculties = !departmentFilter 
    ? faculties 
    : faculties.filter(f => f.dept_id === departmentFilter);

  const handleEnroll = async (courseId) => {
    try {
      await axios.post('/api/enrollments', {
        student_id: actualStudentId,
        course_id: courseId,
        enrollment_date: new Date().toISOString().split('T')[0]
      });
      setToast({ type: 'success', message: 'Successfully enrolled in course!' });
      loadDashboardData();
    } catch (error) {
      setToast({ type: 'error', message: 'Failed to enroll in course' });
    }
  };

  const loadCourseDetails = async (courseId) => {
    try {
      if (!actualStudentId) throw new Error('Student ID not found');

      const [courseRes, weeksRes] = await Promise.all([
        axios.get(`/api/courses/${courseId}`),
        axios.get(`/api/weeks/flow/${actualStudentId}/${courseId}`)
      ]);

      setCourseDetails(courseRes.data);
      setWeekProgress(weeksRes.data?.weeks || []);
      setSelectedCourse(courseId);
      setActiveSection('learning');
    } catch (error) {
      setToast({ type: 'error', message: 'Failed to load course details' });
    }
  };

  const handleDownloadCertificate = async (enrollment) => {
    const progressPercent = Number(enrollment?.progress_percent ?? enrollment?.progress ?? 0);
    if (progressPercent < 100) {
      setToast({ type: 'error', message: 'Certificate can be downloaded only after 100% course completion.' });
      return;
    }
    const downloadUrl = enrollment?.certificate_download_url;
    if (!downloadUrl) {
      setToast({ type: 'error', message: 'Certificate is not available yet. Your faculty needs to publish it first.' });
      return;
    }
    try {
      const res = await fetch(downloadUrl, {
        headers: { 'Accept': 'application/octet-stream' }
      });
      if (!res.ok) {
        setToast({ type: 'error', message: 'Failed to download certificate.' });
        return;
      }
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `certificate_${enrollment.certificate_id || enrollment.course_id}.html`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setToast({ type: 'error', message: 'Failed to download certificate.' });
    }
  };

  const handleLogout = () => {
    localStorage.clear();
    navigate('/');
  };

  const completedCount = enrolledCourses.filter(e => (e.progress_percent || e.progress || 0) >= 100).length;
  const pendingAssignments = enrolledCourses.reduce((sum, e) => sum + (e.pending_assignments || 0), 0);
  const avgScore = enrolledCourses.length > 0
    ? Math.round(enrolledCourses.reduce((sum, e) => sum + (e.progress_percent || e.progress || 0), 0) / enrolledCourses.length)
    : 0;

  if (loading) return <Loader />;

  return (
    <div className="student-dashboard-container">
      {/* ── Sidebar ── */}
      <div className={`sidebar ${sidebarOpen ? 'open' : 'closed'}`}>
        <div className="sidebar-header">
          <div className="sidebar-brand">
            <div className="brand-icon">L</div>
            <h2>LMS Portal</h2>
          </div>
          <button className="toggle-btn" onClick={() => setSidebarOpen(!sidebarOpen)} title={sidebarOpen ? 'Collapse' : 'Expand'}>
            {sidebarOpen ? '◁' : '▷'}
          </button>
        </div>

        <nav className="sidebar-nav">
          {[
            { key: 'dashboard', icon: '📚', label: 'My Courses' },
            { key: 'browse',    icon: '🔍', label: 'Browse Courses' },
            ...(selectedCourse ? [{ key: 'learning', icon: '📖', label: 'Learning' }] : []),
            { key: 'certificates', icon: '🏆', label: 'Certificates' },
            { key: 'profile',   icon: '👤', label: 'My Profile' },
            { key: 'messages',  icon: '💬', label: 'Messages' },
          ].map(item => (
            <button key={item.key}
              className={`nav-item ${activeSection === item.key ? 'active' : ''}`}
              onClick={() => setActiveSection(item.key)}
            >
              <span className="icon">{item.icon}</span>
              <span className="label">{item.label}</span>
            </button>
          ))}
        </nav>

        <div className="sidebar-user">
          <div className="sidebar-user-avatar">{(studentName || 'S').charAt(0).toUpperCase()}</div>
          <div className="sidebar-user-info">
            <div className="sidebar-user-name">{studentName}</div>
            <div className="sidebar-user-role">Student</div>
          </div>
          <button className="sidebar-logout-btn" onClick={handleLogout} title="Logout">⏻</button>
        </div>
      </div>

      {/* ── Main Content ── */}
      <div className="main-content">
        {/* Top Navigation Bar */}
        <div className="topbar">
          <div className="topbar-left">
            <span className="topbar-title">{sectionTitles[activeSection] || 'Dashboard'}</span>
          </div>
          <div className="topbar-right">
            <button className="topbar-icon-btn" onClick={() => setActiveSection('messages')} title="Messages">💬</button>
            <div className="topbar-avatar" onClick={() => setActiveSection('profile')} title="Profile">
              {(studentName || 'S').charAt(0).toUpperCase()}
            </div>
          </div>
        </div>

        <div className="page-content">
        {/* Welcome Section */}
        {activeSection === 'dashboard' && (
          <div className="welcome-section">
            <div className="welcome-content">
              <h1>Welcome back, {studentName}</h1>
              <p>Continue your learning journey and explore new courses</p>
            </div>
            <div className="welcome-stats">
              <div className="stat-card">
                <div className="stat-value">{enrolledCourses.length}</div>
                <div className="stat-label">Enrolled</div>
              </div>
              <div className="stat-card">
                <div className="stat-value">{completedCount}</div>
                <div className="stat-label">Completed</div>
              </div>
              <div className="stat-card">
                <div className="stat-value">{avgScore}%</div>
                <div className="stat-label">Avg Score</div>
              </div>
            </div>
          </div>
        )}

        {/* Dashboard - My Courses */}
        {activeSection === 'dashboard' && (
          <div className="section-content">
            <h2>📚 My Enrolled Courses</h2>
            {enrolledCourses.length === 0 ? (
              <div className="empty-state">
                <div className="empty-icon">📚</div>
                <h3>No courses enrolled yet</h3>
                <p>Browse available courses and start learning!</p>
                <button 
                  className="btn-primary" 
                  onClick={() => setActiveSection('browse')}
                >
                  Browse Courses
                </button>
              </div>
            ) : (
                <div className="courses-grid">
                {enrolledCourses.map(enrollment => {
                  const progressPercent = Number(enrollment.progress_percent ?? enrollment.progress ?? 0);
                  const canDownloadCertificate = progressPercent >= 100 && enrollment.certificate_available;

                  return (
                  <div key={enrollment.course_id} className="course-card">
                    <div className="course-header">
                      <h3>{enrollment.course_name || enrollment.course_title}</h3>
                      <span className={`badge badge-${enrollment.status}`}>
                        {enrollment.status}
                      </span>
                    </div>

                    <div className="course-progress">
                      <ProgressRing percentage={enrollment.progress_percent || enrollment.progress || 0} size={90} strokeWidth={7} />
                    </div>

                    <div className="course-info">
                      <div className="info-item">
                        <span className="icon">📅</span>
                        <span>{enrollment.duration_weeks} weeks</span>
                      </div>
                      <div className="info-item">
                        <span className="icon">😊</span>
                        <span>Week {enrollment.current_week || 1}</span>
                      </div>
                    </div>

                    <button
                      className="btn-primary btn-block"
                      onClick={() => loadCourseDetails(enrollment.course_id)}
                    >
                      {String(enrollment.status || '').toLowerCase() === 'completed' ? 'Review Materials' : 'Continue Learning'}
                    </button>
                    {canDownloadCertificate && (
                      <button
                        className="btn-secondary btn-block"
                        onClick={() => handleDownloadCertificate(enrollment)}
                      >
                        Download Certificate
                      </button>
                    )}
                  </div>
                )})}
              </div>
            )}
          </div>
        )}

        {/* Browse Courses */}
        {activeSection === 'browse' && (
          <div className="section-content">
            <h2>🔍 Browse All Courses</h2>
            
            {/* Filters */}
            <div className="filters-section">
              <div className="filter-group">
                <label>Search by course name</label>
                <input
                  type="text"
                  placeholder="Search courses..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="filter-input"
                />
              </div>

              <div className="filter-group">
                <label>Department</label>
                <select
                  value={departmentFilter}
                  onChange={(e) => {
                    setDepartmentFilter(e.target.value);
                    setFacultyFilter('');
                  }}
                  className="filter-select"
                >
                  <option value="">All Departments</option>
                  {departments.map(dept => (
                    <option key={dept.dept_id} value={dept.dept_id}>
                      {dept.dept_name || dept.department_name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="filter-group">
                <label>Faculty</label>
                <select
                  value={facultyFilter}
                  onChange={(e) => setFacultyFilter(e.target.value)}
                  className="filter-select"
                >
                  <option value="">All Faculties</option>
                  {filteredFaculties.map(faculty => (
                    <option key={faculty.fac_id} value={faculty.fac_id}>
                      {faculty.faculty_name || faculty.fac_name}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Courses Display */}
            {filteredCourses.length === 0 ? (
              <div className="empty-state">
                <div className="empty-icon">🔍</div>
                <h3>No courses found</h3>
                <p>Try adjusting your filters or search term</p>
              </div>
            ) : (
              <div className="courses-grid">
                {filteredCourses.map(course => (
                  <div key={course.course_id} className="course-card course-card-available">
                    <div className="course-header">
                      <h3>{course.course_title || course.course_name}</h3>
                    </div>

                    <p className="course-description">
                      {(course.description || 'No description').substring(0, 120)}...
                    </p>

                    <div className="course-meta">
                      <span className="badge badge-info">{course.program_name || 'Program'}</span>
                    </div>

                    <div className="course-info">
                      <div className="info-item">
                        <span className="icon">📅</span>
                        <span>{course.duration_weeks} weeks</span>
                      </div>
                      <div className="info-item">
                        <span className="icon">👥</span>
                        <span>{course.enrolled_count || 0} enrolled</span>
                      </div>
                    </div>

                    <button
                      className="btn-primary btn-block"
                      onClick={() => handleEnroll(course.course_id)}
                    >
                      Enroll Now
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Learning View */}
        {activeSection === 'learning' && courseDetails && (
          <div className="section-content learning-view">
            <div className="course-hero">
              <div className="course-hero-content">
                <h2>{courseDetails.course_name || courseDetails.course_title}</h2>
                <p>{courseDetails.description}</p>
              </div>
            </div>

            <div className="weekly-progress-section">
              <h3>📅 Your Learning Path</h3>
              <div className="weeks-timeline">
                {weekProgress.map((week, idx) => (
                  <div key={idx} className={`week-item week-${week.status}`}>
                    <div className="week-indicator">
                      {week.status === 'completed' && <span>✓</span>}
                      {week.status === 'current' && <span>▶</span>}
                      {week.status === 'locked' && <span>🔒</span>}
                    </div>
                    <div className="week-info">
                      <h4>Week {week.week_number}</h4>
                      <p>{week.title}</p>
                    </div>
                    <div className={`week-status status-${week.status}`}>
                      {week.status === 'completed' && 'Completed'}
                      {week.status === 'current' && 'In Progress'}
                      {week.status === 'locked' && 'Locked'}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {weekProgress.find(w => ['current', 'unlocked', 'completed'].includes(w.status)) && (
              <div className="current-week-section">
                <h3>📖 This Week's Learning</h3>
                <WeekLearningView 
                  courseId={selectedCourse} 
                  course={courseDetails} 
                  studentId={actualStudentId}
                />
              </div>
            )}
          </div>
        )}

        {/* Certificates Section */}
        {activeSection === 'certificates' && actualStudentId && (
          <div className="section-content">
            <StudentCertificates studentId={actualStudentId} />
          </div>
        )}

        {/* Profile Section */}
        {activeSection === 'profile' && (
          <div className="section-content">
            <h2>👤 My Profile</h2>
            {studentProfile ? (
              <div className="profile-card">
                <div className="profile-header">
                  <div className="profile-avatar">
                    <span>{(studentProfile.student_name || studentProfile.std_name || 'S').charAt(0).toUpperCase()}</span>
                  </div>
                  <div className="profile-info">
                    <h3>{studentProfile.student_name || studentProfile.std_name || studentName}</h3>
                    <p className="profile-id">Student ID: {studentProfile.std_id}</p>
                  </div>
                </div>

                <div className="profile-details">
                  <div className="detail-row">
                    <span className="detail-label">📧 Email</span>
                    <span className="detail-value">{studentProfile.email || 'Not provided'}</span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label">📱 Phone</span>
                    <span className="detail-value">{studentProfile.phone || 'Not provided'}</span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label">📅 Enrollment Date</span>
                    <span className="detail-value">
                      {(studentProfile.enrollment_date || studentProfile.admitted_date)
                        ? new Date(studentProfile.enrollment_date || studentProfile.admitted_date).toLocaleDateString() 
                        : 'Not available'}
                    </span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label">Program</span>
                    <span className="detail-value">{studentProfile.program_name || 'Not available'}</span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label">Batch</span>
                    <span className="detail-value">{studentProfile.batch_name || 'Not available'}</span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label">📚 Courses Enrolled</span>
                    <span className="detail-value">{studentProfile.enrollment_count || 0}</span>
                  </div>
                </div>
              </div>
            ) : (
              <div className="empty-state">
                <div className="empty-icon">👤</div>
                <h3>Profile Information Not Available</h3>
                <p>Unable to load your profile at this moment</p>
              </div>
            )}
          </div>
        )}

        {/* Messages */}
        {activeSection === 'messages' && actualStudentId && (
          <div className="section-content messages-view">
            <StudentCommunication studentId={actualStudentId} studentName={studentName} />
          </div>
        )}

        </div>{/* end page-content */}
      </div>{/* end main-content */}

      {/* Toast */}
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
    </div>
  );
};

export default StudentDashboard;
